import { Exterior, Lead, LeadCustomerDetails, LeadMinimal } from "./lead";
import {
  EcomPlanAddon,
  EcomProductPlan,
  EcomProductType,
  EcomVoucher,
  EcomAccessory,
  EcomCoupon,
  EcomAccessoryPackage,
  EcomPackageAccessory,
  EcomProductPlanOption,
} from "./ecom";
import { EcomAddon } from "./ecom/addon";
import { EcomCampaign, EcomCampaignFinanceOffer } from "./ecom/campaign";
import { OwnerUser, OAuth2ClientOwner } from "./user";
import { VehicleModelMinimal } from "./vehicleModal";
import { VehicleVariantMinimal } from "./vehicleVariant";
import { MediaFile } from "./media";
import { Dict } from "../../models";
import { DiscountTarget, IDiscountType } from "./discount";
import {
  ExcessAmountAdjustmentTypes,
  TradeInSettlementFinanceOwingBank,
} from "./masterData";
import { PaymentRequestCreatedFor } from "../..";
import { OorjitBankDetail } from "../../oorjit-api";

export interface TradeInInfo {
  _id: string;
  modelYear: string;
  brandName: string;
  modelCode: string;
  variant: string;
  trimCode: string;
  color: string;
  chassisNumber: string;
  cubicCapacity?: string;
  condition: string;
  mileage: number;
  specs: "Kuwait" | "Non-Kuwait";
  registrationNumber: string;
  ownership: string;
  ownerName: string;
  hasHypothecation?: boolean;
  hypothecationAmount?: number;
  expectedTradeInValue?: number;
  photos?: MediaFile[];
  loadDocuments?: MediaFile[];
  comments?: string;
  status?: TradeInStatus;
  createdBy?: OwnerUser;
  createdByClient?: OAuth2ClientOwner;
  approvedBy?: OwnerUser;
  approvedAt?: Date;
  evaluation: TradeInEvaluation;
  assist?: TradeInAssist;
  actions: ConfigurationAction[];
  attached?: boolean;
  createdAt?: Date;
  settlement?: TradeInSettlement;
  autolinCode?: string;
  isValidForInvoicing?: boolean;
}

export interface TradeInAssist {
  amount: number;
  comments: string;
  appliedBy?: OwnerUser;
  appliedAt?: string | Date;
}

export enum TradeInStatus {
  Pending = "Pending",
  Approved = "Approved",
  Hold = "Hold",
  EvaluatorRejected = "EvaluatorRejected",
  CustomerRejected = "CustomerRejected",
}

export type ConfigurationStep =
  | "lead"
  | "vehicle"
  | "campaign"
  | "tplRegistration"
  | "accessories"
  | "insurance"
  | "addons"
  | "tradeIn"
  | "finance"
  | "done"
  | "vin";

export type ConfigurationBuildStep = "vehicle" | "build";

export enum LeadConfigurationType {
  Configuration = "Configuration",
  Quotation = "Quotation",
  Booking = "Booking",
  Order = "Order",
  Invoice = "Invoice",
}

export enum ConfigurationVinStatus {
  NotAdded = "NotAdded",
  Reserved = "Reserved",
  Expired = "Expired",
  Requested = "Requested",
  Transferred = "Transferred",
}

export enum ConfigurationStatus {
  Cancelled = "Cancelled",
  Active = "Active",
}

export enum ConfigurationApprovalStatus {
  Pending = "Pending",
  Approved = "Approved",
  Rejected = "Rejected",
}
export enum QuotationStatus {
  Active = "Active",
  Expired = "Expired",
}

export enum InvoiceStatus {
  Invoiced = "Invoiced",
  Cancelled = "Cancelled",
}

export enum RefundApprovalLevel {
  Manager = "Manager",
  Cashier = "Cashier",
}

export interface LeadQuotation {
  _id?: string;
  createdAt?: string | Date;
  expiryAt?: string | Date;
  createdBy?: OwnerUser;
  createdByClient?: OAuth2ClientOwner;
  quotationNumber: string;
  status: QuotationStatus;
}

export interface LeadInvoice {
  _id?: string;
  firstInvoicedAt?: string | Date;
  firstInvoicedBy?: OwnerUser;
  firstInvoicedByClient?: OAuth2ClientOwner;
  invoiceCount?: number;
  lastInvoicedAt?: string | Date;
  lastInvoicedBy?: OwnerUser;
  lastInvoicedByClient?: OAuth2ClientOwner;
  status: InvoiceStatus;
  lastCancelledAt?: string | Date;
  lastCancelledBy: OwnerUser;
  lastCancelledByClient?: OAuth2ClientOwner;
  invoiceNumber?: string;
  dontSendToAutoline?: boolean;
  invoiceCreatedDate?: Date;
  registrationDate?: Date;
  actualDeliveryDate?: Date;
}

interface VINExtensionRequest {
  approvalStatus: ConfigurationApprovalStatus;
  toDate: Date;
  requestedAt?: Date;
  requestedBy?: OwnerUser;
  approvedAt?: Date;
  approvedBy?: OwnerUser;
  rejectedAt?: Date;
  rejectedBy?: OwnerUser;
}

export interface ConfigurationVINNumber {
  vehicleId?: string;
  vin?: string;
  isVinAssigned?: boolean;
  vinAssignedAt?: Date;
  vsbNumber?: string;
  xco?: string;
  autolineStatus?: string;
  autolineProgressCode?: string;
  createdAt?: string | Date;
  addedAt?: string | Date;
  expiryAt?: string | Date;
  requestedAt?: string | Date;
  requestedBy?: OwnerUser;
  transferredAt?: string | Date;
  transferredBy?: OwnerUser;
  createdBy?: OwnerUser;
  createdByClient?: OAuth2ClientOwner;
  vinAddedBy?: OwnerUser;
  status: ConfigurationVinStatus;
  extensionRequest?: VINExtensionRequest;
  approvalStatus?: ConfigurationApprovalStatus;
  approvedAt?: Date;
  approvedBy?: OwnerUser;
  rejectedAt?: Date;
  rejectedBy?: OwnerUser;
  isCreatedAsPreVin?: boolean;
  isPreVin?: boolean;
  preVinIndex?: number;
  expectedArrivalDate?: string | Date;
  deliveryStatus?: VehicleDeliveryStatus;
  registrationStatus?: VehicleRegistrationStatus;
  saleDate?: string | Date;
  comment?: string;
  damageWaiverFile?: MediaFile;
  isTraderVin: boolean;
  gmReportedDate?: Date;
  registrationDeadline?: Date;
}

export interface VehicleDeliveryStatus {
  markAsDelivered?: boolean;
  deliveredDate: Date;
  updatedBy?: OwnerUser;
  nextFollowUpDate?: Date;
  levelActivity?: any[];
  level?: string;
  completedAt?: Date;
  deliveredTime?: string;
}

export interface VehicleRegistrationStatus {
  submitedDetails?: VehicleRegistrationSubmitedStatus;
  completedDetails?: VehicleRegistrationCompletedStatus;
}

export interface VehicleRegistrationCompletedStatus {
  status?: RegistrationStatus;
  submittedDate?: Date;
  updatedBy?: OwnerUser;
  registrationNo?: string;
}

export interface VehicleRegistrationSubmitedStatus {
  status?: RegistrationStatus;
  submittedDate: Date;
  updatedBy?: OwnerUser;
}

export enum RegistrationStatus {
  Submitted = "Submitted",
  Completed = "Completed",
}

export enum TransactionPaymentMethod {
  Cash = "cash",
  KNet = "KNet",
  Cheque = "cheque",
  BankTransfer = "bankTransfer",
  CreditCard = "card",
  AmericanExpress = "americanExpress",
  Online = "online",
  Finance = "finance",
  TradeIn = "trade-in",
  Transfer = "transfer",
  WebPayment = "webPayment",
}

export enum PaymentStatus {
  Paid = "Paid",
  Received = "Received",
}

export enum PaymentType {
  Payment = "Payment",
  Refund = "Refund",
}

export enum ApprovalStatus {
  Pending = "Pending",
  Approved = "Approved",
  Rejected = "Rejected",
  Requested = "Requested",
  Processed = "Processed",
  Deleted = "deleted",
  Completed = "completed",
  Received = "Received",
}

export interface CardDetails {
  bank: string;
  lastDigits: string;
  cardType: string;
  approvalCode: string;
  date: Date;
}

export interface Transaction {
  _id: string;
  amount: number;
  paymentMethod: TransactionPaymentMethod;
  leadId?: string;
  configId?: string;
  card?: CardDetails;
  chequeDetails?: {
    bank: string;
    receipt: MediaFile;
    date: Date;
    accountNumber?: string;
  };
  bankTransferDetails?: {
    bank: string;
    receipt: MediaFile;
    date: Date;
    accountNumber?: string;
    transferConfirmationNumber?: string;
  };
  cashPaymentDetails?: {
    date: Date;
  };
  onlineTransactionDetails?: { transactionId: string };
  createdAt?: Date | string;
  createdBy?: OwnerUser;
  createdByClient?: OAuth2ClientOwner;
  receivedBy?: OwnerUser;
  receivedByClient?: OAuth2ClientOwner;
  receivedAt?: Date;
  status?: PaymentStatus;
  isRefund?: boolean;
  approvalStatus: ApprovalStatus;
  comments?: string;
  approvalComments?: string;
  rejectionComments?: string;
  receipt?: MediaFile;
  transferDetails?: {
    _id: string;
    comments: string;
    date: Date;
    configToId: string;
    transferToQuotationNumber: string;
    transferFromQuotationNumber: string;
    transferredPaymentDate?: Date;
  };
  type?: string;
  isRefunded?: boolean;
  isTransferred?: boolean;
  paidBy?: string;
  expiry?: Date;
  showroomId?: string;
  quotationNumber?: string;
  customer?: LeadCustomerDetails;
  transactionType?: string;
  paymentRequestId?: string;
  requestAddedAgainst?: string;
  paymentRequestCreatedFor?: PaymentRequestCreatedFor;
  paymentApiTransactionId?: {
    depositDocNo?: string;
    depositMagicNo?: string;
  };
  receiptNo?: string;
  approvedAt?: Date | string;
  transferredToCustomerTransaction?: boolean;
}

export interface AutolinePayments {
  receiptNumber?: string;
  receiptnumber?: string;
  depositamount?: number;
  customername?: string;
  customermagic?: string;
  depositstatus?: string;
  depositdate?: Date;
}

export enum BookingPaymentMethod {
  Card = "card",
  Cash = "cash",
  WithoutPayment = "WithoutPayment",
}

export interface LeadBooking {
  createdAt: Date;
  createdBy?: OwnerUser;
  createdByClient?: OAuth2ClientOwner;
  amount?: number;
  transaction?: Transaction;
  paymentMethod: BookingPaymentMethod;
}

export interface LeadPreBooking {
  createdAt?: Date;
  createdBy?: OwnerUser;
  createdByClient?: OAuth2ClientOwner;
  bookedAt?: Date;
  bookedBy?: OwnerUser;
  bookedByClient?: OAuth2ClientOwner;
  amount?: number;
  transaction?: Transaction;
  paymentMethod?: BookingPaymentMethod;
  vehicle?: ConfigurationVehicle;
  isBooked?: boolean;
}

export enum LeadOrderStatus {
  Pending = "Pending",
  Confirmed = "Confirmed",
  Cancelled = "Cancelled",
  Delivered = "Delivered",
}

export interface LeadOrder {
  createdAt: Date;
  createdBy?: OwnerUser;
  createdByClient?: OAuth2ClientOwner;
  cancelledAt?: Date;
  cancelledBy?: OwnerUser;
  cancelledByClient?: OAuth2ClientOwner;
  confirmedAt?: Date;
  confirmedBy?: OwnerUser;
  confirmedByClient?: OAuth2ClientOwner;
  deliveredAt?: Date;
  deliveredBy?: OwnerUser;
  deliveredByClient?: OAuth2ClientOwner;
  status: LeadOrderStatus;
  expectedDeliveryDate?: Date;
  invoiceEnabled?: boolean;
}

export interface ConfigurationAccessory extends EcomAccessory {
  _id?: string;
  campaignID?: string;
  discount?: number;
  isPushedToOneDrive?: boolean;
  pushedBy?: OwnerUser;
  pushedAt?: Date;
  pulledBy?: OwnerUser;
  pulledAt?: Date;
  internalPrice?: number;
}

export interface ConfigurationVoucher extends EcomVoucher {
  campaignID: string;
}

export interface ConfigurationMinimal {
  _id?: string;
  quotationNumber: string;
  quotationCreatedAt?: Date;
  vehicle: ConfigurationVehicle;
  vin?: ConfigurationVINNumber;
  configurationType?: LeadConfigurationType;
  status?: ConfigurationStatus;
  customer?: LeadCustomerDetails;
  leadId?: string;
}

export interface LeadConfiguration {
  configurationType: LeadConfigurationType;
  status?: ConfigurationStatus;
  _id?: string;
  leadId: string;
  lead: LeadMinimal;
  customerId: string;
  customer: LeadCustomerDetails;
  vehicle?: ConfigurationVehicle;
  ecomConfigID?: number;
  accessories?: ConfigurationAccessory[];
  accessoryPackages?: ConfigurationAccessoryPackage[];
  coupons?: EcomCoupon[];
  campaignCashbackItems?: ConfigurationCampaignCashBack[];
  vouchers?: ConfigurationVoucher[];
  campaigns?: EcomCampaign[];
  preBookingCampaigns?: EcomCampaign[];
  addons?: EcomAddon[];
  financePrincipalAmount?: number;
  totalPrice?: number;
  tradeIns?: TradeInInfo[];
  insurance?: ConfigurationEcomPlan;
  servicePackage?: ConfigurationEcomPlan;
  customerCare?: ConfigurationEcomPlan;
  extendedWarranty?: ConfigurationEcomPlan;
  tpl?: ConfigurationEcomPlan;
  registration?: ConfigurationEcomPlan;
  finance?: FinanceInfo;
  updatedAt?: string;
  createdAt?: string;
  quotation?: LeadQuotation;
  booking?: LeadBooking;
  preBooking?: LeadPreBooking;
  isPreBooking?: boolean;
  order?: LeadOrder;
  vin?: ConfigurationVINNumber;
  transactions?: Transaction[];
  totalAmountPaid?: number;
  salesPerson?: OwnerUser;
  isParent?: boolean;
  showroomId: string;
  showroom: {
    _id: string;
    title: string;
    costCenter: string;
    accountNumber: string;
    phone: string;
  };
  discount?: Discount;
  discounts?: Discount[];
  campaignCashBackDiscount?: number;
  campaignCouponTotalDiscount?: number;
  vinbasedDiscount?: number;
  cancelRequest: CancelRequestInfo;
  cancelledAt: Date;
  cancelledBy: OwnerUser;
  paymentRequests?: PaymentRequest[];
  totalPoolDiscount?: number;
  totalDirectCashDiscount?: number;
  totalCampaignDiscount?: number;
  totalAccessoriesDiscount?: number;
  totalDiscount?: number;
  interestedAccessories?: ConfigurationAccessory[];
  interestedAccessoryPackages?: ConfigurationAccessoryPackage[];
  interestedInsurance?: ConfigurationEcomPlan;
  interestedServicePackage?: ConfigurationEcomPlan;
  interestedCustomerCare?: ConfigurationEcomPlan;
  interestedExtendedWarranty?: ConfigurationEcomPlan;
  bankOffer?: Discount;
  ownerDiscount?: Discount;
  isOrderEdited?: boolean;
  totalAdjustments?: number;
  isCreatedAsPreBooking?: boolean;
  financeContribution?: Discount;
  excessAmountAdjustments?: ConfigurationExcessAmountAdjustment[];
  totalExcessAmountAdjustments?: number;
  invoice?: LeadInvoice;
  cancelInvoiceRequest: CancelRequestInfo;
  invoiceNumbers?: string[];
  invoiceLockedDate?: Date;
  accessoriesCampaignBalance?: number;
  gmPartnerDiscount?: Discount;
  damagedByFranchise?: Discount;
  collegeGradDiscount?: Discount;
  otherDiscounts?: Discount[];
  history?: {
    invoice?: LeadInvoice;
  }[];
  accessoriesLastPushedDate?: Date;
  isInvoiceWithDue?: boolean;
  isLpo?: boolean;
  isInvoicedFailedInKerridge: boolean;
}

export interface OtherDiscount {
  approvalStatus: ConfigurationApprovalStatus;
  _id: string;
  discount: number;
  discountId: string;
  createdAt: Date;
  documents: {
    documentTypeId: string;
    side: number;
    document: MediaFile;
    name?: string;
  }[];
  actions: DiscountAction[];
  approvedBy: OwnerUser;
  approvedAt: Date;
  createdBy: OwnerUser;
  comments: string;
  isActive: boolean;
  rejectedBy?: OwnerUser;
  rejectedAt?: Date;
}

export interface ConfigurationCampaignCashBack {
  productID: string;
  productOptionID: string;
  productType: EcomProductType;
  campaignID: string;
  cashbackAmount: number;
}

export interface CancelRequestInfo {
  approvalStatus: ConfigurationApprovalStatus;
  _id: string;
  reason: string;
  comments: string;
  refund: {
    _id: string;
    paymentMethod: string;
    cashPaymentDetails: {
      _id: string;
      date: Date;
    };
  };
  createdBy: OwnerUser;
  updatedAt: Date;
  createdAt: Date;
  approvalComments: string;
  approvedAt: Date;
  approvedBy: OwnerUser;
  rejectedAt: Date;
  rejectedBy: OwnerUser;
}

export interface ConfigurationAccessoryPackageOptions {
  tintOptions?: Dict<EcomPackageAccessory> & {
    windsheild?: EcomPackageAccessory;
    rear?: EcomPackageAccessory;
  };
  leatherAccessory?: EcomPackageAccessory;
}

export interface ConfigurationAccessoryPackage
  extends EcomAccessoryPackage,
    ConfigurationAccessoryPackageOptions {
  campaignID?: string;
  discount?: number;
  isPushedToOneDrive?: boolean;
  pushedBy?: OwnerUser;
  pushedAt?: Date;
  pulledBy?: OwnerUser;
  pulledAt?: Date;
}

export interface Discount {
  approvalStatus: ConfigurationApprovalStatus;
  _id: string;
  discount: number;
  discountId: string;
  discountTypeId?: string;
  discountMode: string;
  createdAt: Date;
  documents: {
    documentTypeId: string;
    side: number;
    document: MediaFile;
    name?: string;
  }[];
  actions: DiscountAction[];
  approvedBy: OwnerUser;
  approvedAt: Date;
  createdBy: OwnerUser;
  comments: string;
  discountType: IDiscountType;
  target: DiscountTarget;
  isActive: boolean;
  tradeInId?: string;
  otherDiscountName?: string;
  donotAppliedToInsurance?: boolean;
}

export interface DiscountAction {
  _id: string;
  createdAt: Date;
  type: string;
  comment: string;
  createdBy: OwnerUser;
  data: { discount: number; npc: number };
}

export interface ConfigurationEcomPlanOptions {
  productOptionID: string;
  productOptionName: string;
  installments: number;
  paymentType: "cash" | "installments";
  priceType: "percentage" | "fixed";
  priceValue: number;
  price?: number;
  addOns?: EcomPlanAddon[];
  payCollectBy?: string;
  periodOfService?: number;
  periodUnit?: string;
  periodStartFrom?: string;
  includeAccessoriesPrice?: boolean;
  minimumAmount?: number;
  sumInsuredDeduction?: number;
  planOption?: ProductPlanOption[];
  excessAmount?: number;
  menuCode: string;
  internalpriceType?: "percentage" | "fixed";
  internalpriceValue?: number;
  oorjitCode?: string;
}

export interface ProductPlanOption {
  minimumAmount?: number;
  sumInsuredDeduction?: number;
  periodOfService?: number;
  periodUnit?: string;
  priceType: "percentage" | "fixed";
  priceValue: number;
  excessAmount?: number;
  productOptionID?: string;
}

export interface ConfigurationEcomPlan
  extends EcomProductPlan,
    ConfigurationEcomPlanOptions {
  campaignID?: string;
  discount?: number;
  couponID?: string;
  upgradedPackage?: ConfigurationEcomPlan;
}

export enum FinanceStatus {
  Pending = "Pending",
  Approved = "Approved",
  Hold = "Hold",
  Rejected = "Rejected",
}

export interface FinanceInfo {
  bank: OorjitBankDetail;
  effectiveInterestRate?: number;
  monthlyPayment: number;
  downPayment: number;
  term: number;
  borrower?: FinanceBorrowerInfo;
  lpo?: FinanceLPO;
  actions?: ConfigurationAction[];
  status?: FinanceStatus;
  approvedBy?: OwnerUser;
  approvedAt?: Date | string;
  approvedByClient?: OAuth2ClientOwner;
  attached?: boolean;
  campDiscountPercentage?: number;
  campCashback?: number;
  numOfFreeEmi?: number;
  campaignID?: string;
}

export interface FinanceBorrowerInfo {
  _id?: string;
  borrowerType: string;
  borrowerFrom: string;
  name: string;
  civilId?: string;
  relationship?: string;
  jobType?: string;
  employerName?: string;
  typeOfEmployment?: string;
  hiringDate?: Date | string;
  monthlyIncome?: number;
  bankAccountNumber?: string;
  existingEmi?: number;
  salaryStatementImage?: MediaFile;
  salaryCertificateImage?: MediaFile;
  passportCopy?: MediaFile;
}

export interface ConfigurationVehicle {
  _id?: string;
  model: VehicleModelMinimal;
  variant: VehicleVariantMinimal;
  basePrice?: number;
  cashbackDiscount?: number;
  exterior: Exterior;
  interior: Exterior;
  ecomCombinationId?: string;
  ecomCombinationSku: string;
  priceFrom?: number;
  priceTo?: number;
  vinNumber?: string;
  vsbNumber?: string;
  xco?: string;
}

export interface LeadConfigurationId {
  leadId: string;
  configId: string;
}

export interface VinRequestHistory {
  approvalStatus: ConfigurationApprovalStatus;
  toDate?: Date;
  extensionRequest?: boolean;
  approvedAt?: Date;
  approvedBy?: OwnerUser;
  requestedAt?: Date;
  requestedBy?: OwnerUser;
  rejectedAt?: Date;
  rejectedBy?: OwnerUser;
  vehicleId: string;
  vin: string;
  leadId: string;
  configId: string;
}

export interface TradeInEvaluation {
  tradeInValue: number;
  validTill: Date | string;
  document?: MediaFile;
  comments?: string;
  exteriorColorCode?: string;
  interiorColorCode?: string;
  physicalLocationCode?: string;
  chassisNumber?: string;
  cubicCapacity?: number;
  mileage?: number;
  lastOwnerName?: string;
  lastOwnerAddress?: {
    line1?: string;
    line2?: string;
  };
  exteriorColor?: {
    colorCode?: string;
    colorDescription?: string;
    colorHex?: string;
  };
  interiorColor?: {
    colorCode?: string;
    colorDescription?: string;
    colorHex?: string;
  };
  physicalLocation?: {
    accountNumber?: string;
    address?: string;
    latitude?: number;
    locationCode?: string;
    longitude?: number;
    name?: string;
  };
}

export interface TradeInSettlement {
  cashbackAmount: number;
  hypothecationAmount: number;
  createdBy?: OwnerUser;
  createdAt?: Date;
  updatedBy?: OwnerUser;
  updatedAt?: Date;
  isFinanceOwing?: boolean;
  financeOwingBankId?: string;
  financeOwingBank?: TradeInSettlementFinanceOwingBank;
  financeType?: string;
  tradeinFinanceOwingDocument?: MediaFile;
  financeOwingClearanceDocument?: MediaFile;
}

export enum ConfigurationActionType {
  Approved = "Approve",
  Rejected = "Reject",
  Hold = "Hold",
  RequestForNegotiation = "RequestForNegotiation",
  SendReminder = "SendReminder",
  Withdraw = "Withdraw",
  EvaluationAdded = "EvaluationAdded",
  Update = "Update",
  TradeInAssistDiscountAdded = "TradeInAssistDiscountAdded",
  TradeInAssistDiscountRemoved = "TradeInAssistDiscountRemoved",
}

export interface ConfigurationAction {
  createdBy: OwnerUser;
  type: ConfigurationActionType;
  data: any;
  createdAt: Date | string;
  comment?: string;
}

export interface FinanceLPO {
  bank: OorjitBankDetail;
  lpoNumber: string;
  lpoDate: Date | string;
  lpoDocument: MediaFile;
  approvedLoanAmount: number;
  tenure?: number;
  interest?: number;
  downPayment?: number;
  monthlyPayment?: number;
  comments?: string;
  isCommissionApplicable?: boolean;
  isCommissionIncludedInLPO?: boolean;
  commissionAmount?: number;
  fsdDocument: MediaFile;
}

export interface VinRequestHistory {
  approvalStatus: ConfigurationApprovalStatus;
  toDate?: Date;
  extensionRequest?: boolean;
  approvedAt?: Date;
  approvedBy?: OwnerUser;
  requestedAt?: Date;
  requestedBy?: OwnerUser;
  rejectedAt?: Date;
  rejectedBy?: OwnerUser;
  vehicleId: string;
  vin: string;
  leadId: string;
  configId: string;
  customerId?: string;
  customer?: LeadCustomerDetails;
  quotationNumber: string;
}

export interface CollectionSummary {
  count: number;
  totalAmount: number;
  paymentMethod: string;
}

export interface TransactionList {
  configurationType: LeadConfigurationType;
  status?: ConfigurationStatus;
  _id?: string;
  leadId: string;
  lead: LeadMinimal;
  customerId: string;
  customer: LeadCustomerDetails;
  vehicle?: ConfigurationVehicle;
  ecomConfigID?: number;
  /* tslint:disable:array-type */
  accessories?: Array<
    EcomAccessory & {
      _id?: string;
    }
  >;
  financePrincipalAmount?: number;
  totalPrice?: number;
  tradeIns?: TradeInInfo[];
  insurance?: ConfigurationEcomPlan;
  servicePackage?: ConfigurationEcomPlan;
  finance?: FinanceInfo;
  updatedAt?: string;
  createdAt?: string;
  quotation?: LeadQuotation;
  booking?: LeadBooking;
  order?: LeadOrder;
  vin?: ConfigurationVINNumber;
  transaction?: Transaction;
  totalAmountPaid?: number;
  salesPerson?: OwnerUser;
  showroomId: string;
  showroom: {
    _id: string;
    title: string;
    costCenter: string;
    accountNumber: string;
  };
  discount?: Discount;
  isAutolineFailed?: boolean;
}

export interface PaymentResponse {
  lead: Lead;
  configuration: LeadConfiguration;
  transaction?: Transaction;
  paymentAgainstConfiguration?: boolean;
  backgroundJobId?: string;
}

export interface CreateTransferPayment {
  customerId?: string;
  configurationId?: string;
  amount?: number;
  comments?: string;
  transferredTransactionIds?: string[];
}

export interface QuotationNumbersList {
  quotationNumber: string;
  configId?: string;
}

export interface PaymentRequest {
  _id: string;
  amount: number;
  requestedBy?: OwnerUser;
  requestedAt?: Date | string;
  status: ApprovalStatus;
  phone?: string;
  expiry?: Date;
  link?: string;
  isRefunded?: boolean;
  paymentRequestCreatedFor?: PaymentRequestCreatedFor;
  isRefund?: boolean;
  approvalStatus?: ApprovalStatus;
  cashPaymentDetails?: {
    date: Date;
  };
  comments?: string;

  leadId?: string;
  configId?: string;
  card?: CardDetails;
  chequeDetails?: {
    bank: string;
    receipt: MediaFile;
    date: Date;
    accountNumber?: string;
  };
  bankTransferDetails?: {
    bank: string;
    receipt: MediaFile;
    date: Date;
    accountNumber?: string;
    transferConfirmationNumber?: string;
  };
  onlineTransactionDetails?: { transactionId: string };
  createdAt?: Date | string;
  createdBy?: OwnerUser;
  createdByClient?: OAuth2ClientOwner;
  receivedBy?: OwnerUser;
  receivedByClient?: OAuth2ClientOwner;
  receivedAt?: Date;
  approvalComments?: string;
  rejectionComments?: string;
  receipt?: MediaFile;
  isTransferred?: boolean;
  paidBy?: string;
  showroomId?: string;
  quotationNumber?: string;
  paymentRequestId?: string;
  requestAddedAgainst?: string;
  paymentMethod?: string;
}

export interface CreateTradeInSettlement {
  hypothecationAmount?: number;
  cashbackAmount?: number;
  financeOwingBankId?: string;
  isFinanceOwing?: boolean;
  financeType?: string;
  tradeinFinanceOwingDocument?: MediaFile;
  financeOwingClearanceDocument?: MediaFile;
}

export interface ConfigurationExcessAmountAdjustment {
  _id?: string;
  excessAmountAdjustmentTypeCode?: string;
  amount?: number;
  excessAmountAdjustmentType?: ExcessAmountAdjustmentTypes;
}

export interface Invoice {
  updatedAt?: Date;
  firstInvoicedAt: Date;
  firstInvoicedBy?: OwnerUser;
  firstInvoicedByClient?: OAuth2ClientOwner;
  lastInvoicedAt?: Date;
  lastInvoicedBy?: OwnerUser;
  lastInvoicedByClient?: OAuth2ClientOwner;
  lastCancelledAt?: Date;
  lastCancelledBy?: OwnerUser;
  lastCancelledByClient?: OAuth2ClientOwner;
  status: InvoiceStatus;
  invoiceCount: number;
}

export interface PreBookingVehicleCombinations {
  model: {
    code: string;
    modelCode: string;
    name: string;
    year: string;
  };
  variant: {
    basePrice: number;
    bookingAmount: number;
    cashbackDiscount: number;
    ecomId: string;
    name: string;
    variantCode: string;
    variantSku: string;
    year: string;
  };
  combinations: PreBookingCombinations[];
}

export interface PreBookingCombinations {
  interior: {
    colorCode: string;
    colorDescription: string;
    colorHex: string;
    images: string[];
  };
  exterior: {
    colorCode: string;
    colorDescription: string;
    colorHex: string;
    images: string[];
  };
  sku: string;
  ecomId: string;
}

export interface PreBookingVehicle {
  _id?: string;
  model: {
    code: string;
    modelCode: string;
    name: string;
    year: string;
  };
  variant: {
    basePrice?: number;
    bookingAmount?: number;
    cashbackDiscount?: number;
    ecomId: string;
    name: string;
    variantCode: string;
    variantSku?: string;
    year: string;
  };
  interior?: {
    colorCode: string;
    colorDescription: string;
    colorHex: string;
    images?: string[];
    image?: string;
  };
  exterior?: {
    colorCode: string;
    colorDescription: string;
    colorHex: string;
    images?: string[];
    image?: string;
  };
  sku: string;
  vehicleType?: string;
  price?: number;
  tplPrice?: number;
  isActive?: boolean;
  isDeleted?: boolean;
  cashbackDiscount?: number;
  ecomHash?: string;
  ecomId?: string;
  ecomSKU?: string;
}

export interface CreatePreBooking
  extends Omit<LeadConfiguration, "vehicle" | "lead"> {
  leadId: string;
  vehicle: {
    interiorColorCode: string;
    exteriorColorCode: string;
    modelCode: string;
    year: string;
    variantCode: string;
  };
  vehicleId: string;
  mode: string;
  request?: boolean;
}

export interface ConfigurationExcessAmountAdjustment {
  _id?: string;
  excessAmountAdjustmentTypeCode?: string;
  amount?: number;
  excessAmountAdjustmentType?: ExcessAmountAdjustmentTypes;
}

export enum RequestTypeStatus {
  VINRequest = "VIN Number",
  ExtensionRequest = "Extension",
  RefundRequest = "Refund",
  PoolDiscountRequest = "Pool Discount",
  SpecialRequest = "Special",
  CashDiscount = "Cash Discount",
  OtherDiscount = "Other Discount",
  OrderCancelRequest = "Order Cancel Request",
  InvoiceCancelRequest = "Invoice Cancel Request",
}

export enum RequestTypeStage {
  Lead = "Lead",
  Enquiry = "Enquiry",
  Configuration = "Configuration",
  Quotation = "Quotation",
  Booking = "Booking",
  OrderInProgress = "OrderInProgress",
  OrderConfirmed = "OrderConfirmed",
  Invoice = "Invoice",
}
export interface EcomAccessoryItem {
  productCategoryName?: string;
  salesPrice?: number;
  offerPrice?: number;
  productImage?: string;
  productPreviewImage?: string;
  productType?: string;
  tintAvailable?: boolean;
  checkRows?: boolean;
  tintRows?: number;
  partNumber?: string;
  menuCode?: string;
  componentCode?: string;
  productCurrency?: string;
  productID?: number;
  leatherPreferance?: string;
  displayDisclaimer?: string;
  productDefaultCurrency?: number;
  productOptionRegularPrice?: number;
  productTitle?: string;
  title?: string;
  productDescription?: string;
  accessories?: string[];
  type?: string;
  packageID?: number;
  packageName?: string;
  packageDescription?: string;
  packageImage?: string;
  packagePrice?: number;
  accessoryId?: string;
}

export interface UploadedCombinations {
  validationErrors: string[];
  isDeleted: boolean;
  _id: string;
  rawData: string;
  year: string;
  variantCode: string;
  exteriorColorCode: string;
  interiorColorCode: string;
  overrideWithVariantPrice: boolean;
  disableVinPrice: boolean;
  price: number;
  isValid: boolean;
}

export interface CombinationPriceUploadResponse {
  inValidCount: number;
  validCount: number;
  totalCount: number;
  _id: string;
  date: Date;
  createdBy: OwnerUser;
  combinations: UploadedCombinations[];
  createdAt: Date;
  updatedAt: Date;
}

export enum AccessoryStages {
  Lead = "Lead",
  Enquiry = "Enquiry",
  Configuration = "Configuration",
  Quotation = "Quotation",
  Booking = "Booking",
  Order = "Order",
  OrderConfirmation = "OrderConfirmation",
  Invoiced = "Invoiced",
}
